import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentChartBarIcon,
  FolderPlusIcon,
  FunnelIcon,
  PencilSquareIcon,
  PlusIcon,
  ShareIcon,
  TrashIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { VideoCameraIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Share from "../components/matches/Share";
import { useAuthStatus } from "../hooks/useAuthStatus";
import {
  deleteMatch,
  fetchAllMatches,
  fetchAllPlayers,
  fetchAllTeams,
  fetchMatchByGuid,
  fetchMatchWithData,
  fetchPlayerByGuid,
  fetchSharedMatches,
  fetchTeamByGuid,
  mergePlayerRecords,
  saveMatch,
  savePlayer,
  saveTeam,
  updateMatchShare,
  updatePlayerEnrichment,
  upsertPlayer,
  upsertTeam,
} from "../utils/dbutils";
import { enrichPlayerFromWikidata } from "../utils/playerEnricher";
import { kKeypoint, kRally } from "../utils/pstsDefines";
import { importPSTS } from "../utils/pstsFile";
import { uuid } from "../utils/utils";

const ROUNDS = [
  { label: "Q1", number: 1 },
  { label: "Q2", number: 2 },
  { label: "Q3", number: 3 },
  { label: "Q4", number: 4 },
  { label: "R1", number: 5 },
  { label: "R2", number: 6 },
  { label: "R3", number: 7 },
  { label: "R4", number: 8 },
  { label: "R5", number: 9 },
  { label: "Quarter Final", number: 10 },
  { label: "Semi Final", number: 11 },
  { label: "Final", number: 12 },
];

const DEFAULT_SET_CONFIG = {
  tiebreakAfterGame: 12,
  tiebreakTo: 7,
  tiebreakMargin: 2,
};

/**
 * Syncs players from a match to the "players" collection.
 * - If a player's GUID already exists in DB → skip (already there).
 * - Otherwise → create a new player record immediately.
 * - Additionally, if a player with the same name already exists under a DIFFERENT GUID,
 *   include it in the returned conflicts list so the caller can ask the user whether to merge.
 * Returns { conflicts: [{ pstsPlayer, existingPlayer }] }.
 */
async function syncPlayersFromMatch(match, uid, existingPlayers = []) {
  if (!uid) return { conflicts: [] };
  const matchPlayers = match.isDoubles
    ? [
        match.teamA?.player1,
        match.teamA?.player2,
        match.teamB?.player1,
        match.teamB?.player2,
      ].filter(Boolean)
    : [match.player1, match.player2].filter(Boolean);

  const conflicts = [];
  for (const p of matchPlayers) {
    if (!p?.guid) continue;
    // 1. GUID already in DB → nothing to do
    const byGuid = await fetchPlayerByGuid(uid, p.guid);
    if (byGuid) {
      // Still check for a same-name duplicate so the user can merge if they want
      const fn = (p.firstName ?? "").trim().toLowerCase();
      const ln = (p.lastName ?? "").trim().toLowerCase();
      const nameMatch = existingPlayers.find(
        (ep) =>
          ep.guid !== p.guid &&
          (ep.firstName ?? "").trim().toLowerCase() === fn &&
          (ep.lastName ?? "").trim().toLowerCase() === ln,
      );
      if (nameMatch) conflicts.push({ pstsPlayer: p, existingPlayer: nameMatch });
      continue;
    }

    // 2. Always create the player record with the PSTS GUID
    const playerData = {
      guid: p.guid,
      firstName: p.firstName ?? "",
      lastName: p.lastName ?? "",
      dob: p.dob ?? 0,
      hand: p.hand ?? 0,
      height: p.height ?? 0,
      weight: p.weight ?? 0,
      gender: p.gender ?? "",
      email: p.email ?? "",
      country: p.country ?? null,
    };
    const { isNew, player: saved } = await upsertPlayer(playerData, uid);
    if (isNew || (saved && !saved.enrichedAt)) {
      enrichPlayerFromWikidata(p.firstName, p.lastName)
        .then((enrichment) => {
          if (enrichment) updatePlayerEnrichment(uid, p.guid, enrichment);
        })
        .catch(() => {});
    }

    // 3. Check for a same-name player with a different GUID → offer merge
    const fn = (p.firstName ?? "").trim().toLowerCase();
    const ln = (p.lastName ?? "").trim().toLowerCase();
    const nameMatch = existingPlayers.find(
      (ep) =>
        ep.guid !== p.guid &&
        (ep.firstName ?? "").trim().toLowerCase() === fn &&
        (ep.lastName ?? "").trim().toLowerCase() === ln,
    );
    if (nameMatch) conflicts.push({ pstsPlayer: p, existingPlayer: nameMatch });
  }
  return { conflicts };
}

/**
 * Syncs teams from a doubles match to the "teams" collection.
 * Same duplicate-detection rules as syncPlayersFromMatch.
 */
async function syncTeamsFromMatch(match, uid, existingTeams = []) {
  if (!uid || !match.isDoubles) return { conflicts: [] };
  const computeGuid = (team) =>
    team?.guid ||
    (team?.player1?.guid && team?.player2?.guid
      ? `${team.player1.guid}_${team.player2.guid}`
      : null);

  // Count how many ground shots each player hit from the right (deuce) vs left (ad) court.
  // Normalises across court ends: players at the bottom half (y > 100) keep their x;
  // players at the top half (y < 100) have x mirrored (100 - x) so deuce = right always.
  const computeCourtSideCounts = (playerGuid) => {
    let right = 0, left = 0;
    for (const set of match.sets ?? []) {
      for (const e of set.events ?? []) {
        if (e.playerGuid !== playerGuid) continue;
        if (e.skill !== kRally && e.skill !== kKeypoint) continue;
        if (!e.startBall) continue;
        const [x, y] = e.startBall.split(",").map(Number);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        const nx = y > 100 ? x : (y < 100 ? 100 - x : null);
        if (nx === null) continue;
        if (nx >= 50) right++; else left++;
      }
    }
    return { right, left };
  };

  // Merge new shot counts into existing accumulated counts and derive the dominant side.
  const mergeCounts = (existing, newCounts) => {
    const right = (existing?.right ?? 0) + (newCounts?.right ?? 0);
    const left = (existing?.left ?? 0) + (newCounts?.left ?? 0);
    const total = right + left;
    const rightPct = total > 0 ? Math.round((right / total) * 100) : 50;
    const side = rightPct >= 55 ? "deuce" : rightPct <= 45 ? "ad" : "balanced";
    return { right, left, side };
  };

  const conflicts = [];
  for (const t of [match.teamA, match.teamB].filter(Boolean)) {
    const guid = computeGuid(t);
    if (!guid) continue;

    const p1Guid = t.player1?.guid ?? null;
    const p2Guid = t.player2?.guid ?? null;
    const p1Counts = p1Guid ? computeCourtSideCounts(p1Guid) : null;
    const p2Counts = p2Guid ? computeCourtSideCounts(p2Guid) : null;

    const teamData = {
      guid,
      name: t.name ?? "",
      player1: t.player1
        ? { guid: t.player1.guid, firstName: t.player1.firstName ?? "", lastName: t.player1.lastName ?? "" }
        : null,
      player2: t.player2
        ? { guid: t.player2.guid, firstName: t.player2.firstName ?? "", lastName: t.player2.lastName ?? "" }
        : null,
      ...(p1Counts && { player1CourtSideStats: p1Counts, player1CourtSide: mergeCounts(null, p1Counts).side }),
      ...(p2Counts && { player2CourtSideStats: p2Counts, player2CourtSide: mergeCounts(null, p2Counts).side }),
    };

    const byGuid = await fetchTeamByGuid(uid, guid);
    if (byGuid) {
      // Accumulate court-side stats into the existing team record.
      const p1Merged = p1Counts ? mergeCounts(byGuid.player1CourtSideStats, p1Counts) : null;
      const p2Merged = p2Counts ? mergeCounts(byGuid.player2CourtSideStats, p2Counts) : null;
      const patch = {};
      if (p1Merged) {
        patch.player1CourtSideStats = { right: p1Merged.right, left: p1Merged.left };
        patch.player1CourtSide = p1Merged.side;
      }
      if (p2Merged) {
        patch.player2CourtSideStats = { right: p2Merged.right, left: p2Merged.left };
        patch.player2CourtSide = p2Merged.side;
      }
      if (Object.keys(patch).length > 0) {
        await saveTeam({ ...byGuid, ...patch });
      }
      const nameLower = (t.name ?? "").trim().toLowerCase();
      if (nameLower) {
        const nameMatch = existingTeams.find(
          (et) => et.guid !== guid && (et.name ?? "").trim().toLowerCase() === nameLower
        );
        if (nameMatch) conflicts.push({ pstsTeam: teamData, existingTeam: nameMatch });
      }
      continue;
    }

    await upsertTeam(teamData, uid);

    const nameLower = (t.name ?? "").trim().toLowerCase();
    if (nameLower) {
      const nameMatch = existingTeams.find(
        (et) => et.guid !== guid && (et.name ?? "").trim().toLowerCase() === nameLower
      );
      if (nameMatch) conflicts.push({ pstsTeam: teamData, existingTeam: nameMatch });
    }
  }
  return { conflicts };
}

function Matches() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showNewMatchModal, setShowNewMatchModal] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [showImportLog, setShowImportLog] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState(new Set());
  const [confirmDeleteGuid, setConfirmDeleteGuid] = useState(null); // guid of match pending delete
  const [deleting, setDeleting] = useState(false);
  const [shareTargets, setShareTargets] = useState(null); // guids being shared, or null
  const [savingShare, setSavingShare] = useState(false);
  const [sharedOnly, setSharedOnly] = useState(false); // show only shared matches
  const [filtersExpanded, setFiltersExpanded] = useState(false); // collapsible search panel
  const [playerConflicts, setPlayerConflicts] = useState([]); // { pstsPlayer, existingPlayer }[]
  const [showPlayerConflictModal, setShowPlayerConflictModal] = useState(false);
  const [pendingPlayerConflicts, setPendingPlayerConflicts] = useState([]); // conflicts waiting until after dup modal
  const [resolvingConflicts, setResolvingConflicts] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem("psts_matches_filters");
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          player: parsed.player ?? "",
          tournament: parsed.tournament ?? "",
          dateFrom: parsed.dateFrom ?? "",
          dateTo: parsed.dateTo ?? "",
        };
      }
    } catch {
      /* ignore */
    }
    return { player: "", tournament: "", dateFrom: "", dateTo: "" };
  });

  useEffect(() => {
    try {
      localStorage.setItem("psts_matches_filters", JSON.stringify(filters));
    } catch {
      /* ignore */
    }
  }, [filters]);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    if (uid) loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const loadMatches = async () => {
    setLoading(true);
    const [ms, ps, ts, shared] = await Promise.all([
      fetchAllMatches(uid),
      fetchAllPlayers(uid),
      fetchAllTeams(uid),
      fetchSharedMatches(firebaseUser?.email),
    ]);
    // Merge in matches shared with this user, skipping any the user already owns.
    const ownGuids = new Set(ms.map((m) => m.guid));
    const sharedMatches = (shared ?? []).filter((m) => !ownGuids.has(m.guid));
    const all = [...ms, ...sharedMatches];
    all.sort((a, b) => (b.matchDate ?? 0) - (a.matchDate ?? 0));
    setMatches(all);
    setPlayers(ps ?? []);
    setTeams(ts ?? []);
    setLoading(false);
  };

  const processFiles = async (files) => {
    const fileArray = Array.from(files).filter((f) =>
      f.name.toLowerCase().endsWith(".psts"),
    );
    if (fileArray.length === 0) {
      toast.warning("No .psts files found");
      return;
    }

    setImportLog(
      fileArray.map((f) => ({
        fileName: f.name,
        status: "pending",
        error: "",
      })),
    );
    setShowImportLog(true);
    setProgress({ current: 0, total: fileArray.length });

    const updateLog = (index, update) =>
      setImportLog((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...update };
        return next;
      });

    const parsed = [];
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      setProgress({ current: i + 1, total: fileArray.length });
      updateLog(i, { status: "parsing" });
      try {
        const text = await file.text();
        const match = importPSTS(text);
        if (match) {
          updateLog(i, { status: "checking" });
          parsed.push({ match, fileName: file.name, logIndex: i });
        } else {
          updateLog(i, {
            status: "error",
            error: "Invalid or unsupported format",
          });
        }
      } catch (err) {
        updateLog(i, { status: "error", error: err.message ?? "Read error" });
      }
    }

    setProgress({ current: 0, total: 0 });

    if (parsed.length === 0) return;

    // Separate new vs duplicates
    const newItems = [];
    const dupItems = [];
    for (const item of parsed) {
      const existing = await fetchMatchByGuid(uid, item.match.guid);
      if (existing) {
        dupItems.push(item);
        updateLog(item.logIndex, { status: "duplicate" });
      } else {
        newItems.push(item);
      }
    }

    // Save new matches immediately
    let savedCount = 0;
    const allConflicts = [];
    const seenConflictGuids = new Set();

    for (const item of newItems) {
      const result = await saveMatch(item.match, uid);
      if (!result.error) {
        savedCount++;
        updateLog(item.logIndex, { status: "saved" });
        const { conflicts } = await syncPlayersFromMatch(item.match, uid, players);
        await syncTeamsFromMatch(item.match, uid, teams);
        for (const c of conflicts) {
          if (!seenConflictGuids.has(c.pstsPlayer.guid)) {
            seenConflictGuids.add(c.pstsPlayer.guid);
            allConflicts.push(c);
          }
        }
      } else {
        updateLog(item.logIndex, { status: "error", error: result.error });
      }
    }

    if (savedCount > 0) {
      toast.success(`Saved ${savedCount} new match(es)`);
    }

    if (dupItems.length > 0) {
      setPendingPlayerConflicts(allConflicts);
      setDuplicates(dupItems);
      setShowDuplicateModal(true);
    } else if (allConflicts.length > 0) {
      setPlayerConflicts(allConflicts);
      setShowPlayerConflictModal(true);
    } else {
      loadMatches();
    }

    // Reset file inputs so the same file can be re-imported
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const handleOverwriteAll = async () => {
    setShowDuplicateModal(false);
    let count = 0;
    const seenConflictGuids = new Set(pendingPlayerConflicts.map((c) => c.pstsPlayer.guid));
    const overwriteConflicts = [...pendingPlayerConflicts];
    for (const item of duplicates) {
      const result = await saveMatch(item.match, uid);
      if (!result.error) {
        count++;
        const { conflicts } = await syncPlayersFromMatch(item.match, uid, players);
        await syncTeamsFromMatch(item.match, uid, teams);
        for (const c of conflicts) {
          if (!seenConflictGuids.has(c.pstsPlayer.guid)) {
            seenConflictGuids.add(c.pstsPlayer.guid);
            overwriteConflicts.push(c);
          }
        }
        if (item.logIndex !== undefined)
          setImportLog((prev) => {
            const next = [...prev];
            next[item.logIndex] = { ...next[item.logIndex], status: "saved" };
            return next;
          });
      } else {
        toast.error(`Failed to overwrite ${item.fileName}`);
        if (item.logIndex !== undefined)
          setImportLog((prev) => {
            const next = [...prev];
            next[item.logIndex] = {
              ...next[item.logIndex],
              status: "error",
              error: result.error,
            };
            return next;
          });
      }
    }
    setDuplicates([]);
    setPendingPlayerConflicts([]);
    toast.success(`Overwrote ${count} match(es)`);
    if (overwriteConflicts.length > 0) {
      setPlayerConflicts(overwriteConflicts);
      setShowPlayerConflictModal(true);
    } else {
      loadMatches();
    }
  };

  const handleSkipAll = () => {
    setShowDuplicateModal(false);
    setImportLog((prev) =>
      prev.map((entry) =>
        entry.status === "duplicate" ? { ...entry, status: "skipped" } : entry,
      ),
    );
    setDuplicates([]);
    toast.info("Skipped duplicate matches");
    const pending = pendingPlayerConflicts;
    setPendingPlayerConflicts([]);
    if (pending.length > 0) {
      setPlayerConflicts(pending);
      setShowPlayerConflictModal(true);
    } else {
      loadMatches();
    }
  };

  const handleResolvePlayerConflicts = async (choices) => {
    setResolvingConflicts(true);
    for (const c of playerConflicts) {
      const choice = choices[c.pstsPlayer.guid];
      if (choice === "existing") {
        // Merge: repoint all matches from the PSTS guid → existing player's guid, then delete the PSTS player record
        await mergePlayerRecords(uid, c.pstsPlayer.guid, c.existingPlayer.guid);
      }
      // choice === "new": player was already created with the PSTS guid — nothing more to do
    }
    setResolvingConflicts(false);
    setShowPlayerConflictModal(false);
    setPlayerConflicts([]);
    loadMatches();
  };

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const existingTournaments = [
    ...new Set(matches.map((m) => m.tournament?.name).filter(Boolean)),
  ].sort();

  const handleCreateMatch = async (match) => {
    const result = await saveMatch(match, uid);
    if (result.error) {
      toast.error("Failed to create match: " + result.error);
      return;
    }
    toast.success("Match created");
    setShowNewMatchModal(false);
    syncPlayersFromMatch(match, uid);
    syncTeamsFromMatch(match, uid, teams);
    await loadMatches();
    if (!match.sets) {
      match.sets = [];
    }
    if (match.isDoubles) {
      const teamLabel = (team) =>
        [team?.player1?.lastName, team?.player2?.lastName].filter(Boolean).join("/") ||
        team?.name ||
        "Team";
      match.player1 = { firstName: "", lastName: teamLabel(match.teamA), guid: match.teamA?.guid };
      match.player2 = { firstName: "", lastName: teamLabel(match.teamB), guid: match.teamB?.guid };
    } else {
      match.player1 = {
        firstName: match.player1FirstName,
        lastName: match.player1LastName,
        guid: match.player1Guid,
        country: { name: "", code: match.player1CountryCode },
      };
      match.player2 = {
        firstName: match.player2FirstName,
        lastName: match.player2LastName,
        guid: match.player2Guid,
        country: { name: "", code: match.player2CountryCode },
      };
    }
    const st = {
      player1: match.player1,
      player2: match.player2,
      match: match,
    };

    navigate("/match-coding", { state: st });
  };

  const clearFilters = () =>
    setFilters({ player: "", tournament: "", dateFrom: "", dateTo: "" });

  const handleDeleteMatch = async () => {
    if (!confirmDeleteGuid) return;
    setDeleting(true);
    const result = await deleteMatch(uid, confirmDeleteGuid);
    setDeleting(false);
    setConfirmDeleteGuid(null);
    if (result.error) {
      toast.error("Failed to delete match: " + result.error);
    } else {
      toast.success("Match deleted");
      setSelectedGuids((prev) => { const n = new Set(prev); n.delete(confirmDeleteGuid); return n; });
      setMatches((prev) => prev.filter((m) => m.guid !== confirmDeleteGuid));
    }
  };

  // ── Sharing ────────────────────────────────────────────────────────────────
  const openShare = (guids) => {
    if (!guids || guids.length === 0) return;
    setShareTargets(guids);
  };

  // Initial share settings for the modal, taken from the first target match.
  const shareInitial = () => {
    const targets = shareTargets ?? [];
    const first = matches.find((m) => m.guid === targets[0]);
    // A received (shared) match's `shareStatus`/`shareUsers` belong to the
    // ORIGINAL owner's share, not this user's own — re-sharing always starts
    // fresh instead of inheriting (or exposing) their recipient list.
    if (first?.shared) {
      return { shareStatus: 0, shareUsers: "", sharedBy: first?.sharedBy ?? "" };
    }
    return {
      shareStatus: first?.shareStatus ?? 0,
      shareUsers: first?.shareUsers ?? "",
      sharedBy: "",
    };
  };

  const handleShare = async (result) => {
    if (result === null) {
      setShareTargets(null);
      return;
    }
    const targets = shareTargets ?? [];
    setSavingShare(true);
    let failed = 0;
    const ownerEmail = firebaseUser?.email ?? "";
    for (const guid of targets) {
      // Hydrate the full match (with sets) so a self-contained copy can be
      // written to the shared collection recipients read from.
      const full = await fetchMatchWithData(uid, guid);
      if (!full) {
        failed += 1;
        continue;
      }
      const res = await updateMatchShare(uid, { ...full, ownerEmail }, result);
      if (res?.error) failed += 1;
    }
    setSavingShare(false);
    setMatches((prev) =>
      prev.map((m) =>
        targets.includes(m.guid)
          ? {
              ...m,
              shareStatus: result.shareStatus,
              shareUsers: result.shareStatus === 2 ? result.shareUsers : "",
            }
          : m,
      ),
    );
    setShareTargets(null);
    if (failed > 0) {
      toast.error(`Failed to update sharing for ${failed} match(es)`);
    } else {
      const suffix =
        result.shareStatus === 2 && result.shareUsers
          ? " — recipients notified by email"
          : "";
      toast.success(
        `Sharing updated for ${targets.length} match${targets.length > 1 ? "es" : ""}${suffix}`,
      );
    }
  };

  // Returns the set of player guids present across all currently selected matches
  // Returns the INTERSECTION of player guids across all currently selected matches.
  // e.g. {A,B} selected + {A,C} selected → intersection = {A}
  const teamGuid = (team) =>
    team?.guid ||
    (team?.player1?.guid && team?.player2?.guid
      ? `${team.player1.guid}_${team.player2.guid}`
      : null);

  const commonPlayerGuids = (selectedSet) => {
    let intersection = null;
    for (const m of matches) {
      if (!selectedSet.has(m.guid)) continue;
      const mPlayers = new Set();
      if (m.isDoubles) {
        const tAGuid = teamGuid(m.teamA);
        const tBGuid = teamGuid(m.teamB);
        if (tAGuid) mPlayers.add(tAGuid);
        if (tBGuid) mPlayers.add(tBGuid);
        if (m.teamA?.player1?.guid) mPlayers.add(m.teamA.player1.guid);
        if (m.teamA?.player2?.guid) mPlayers.add(m.teamA.player2.guid);
        if (m.teamB?.player1?.guid) mPlayers.add(m.teamB.player1.guid);
        if (m.teamB?.player2?.guid) mPlayers.add(m.teamB.player2.guid);
      } else {
        if (m.player1Guid) mPlayers.add(m.player1Guid);
        if (m.player2Guid) mPlayers.add(m.player2Guid);
      }
      if (intersection === null) {
        intersection = mPlayers;
      } else {
        for (const g of intersection) {
          if (!mPlayers.has(g)) intersection.delete(g);
        }
      }
    }
    return intersection ?? new Set();
  };

  // A match is selectable if it contains at least one player from the current
  // intersection of all selected matches.
  const isSelectable = (m) => {
    if (selectedGuids.size === 0) return true;
    if (selectedGuids.has(m.guid)) return true; // already selected → always toggleable
    const common = commonPlayerGuids(selectedGuids);
    return (
      (m.player1Guid && common.has(m.player1Guid)) ||
      (m.player2Guid && common.has(m.player2Guid))
    );
  };

  const toggleSelect = (m) => {
    if (!isSelectable(m)) {
      toast.warning(
        "Only matches sharing a common player with all selected matches can be combined.",
      );
      return;
    }
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(m.guid)) next.delete(m.guid);
      else next.add(m.guid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedGuids.size === filteredMatches.length) {
      setSelectedGuids(new Set());
      return;
    }
    // Build a compatible set: greedily add matches that keep the intersection non-empty
    const compatible = new Set();
    for (const m of filteredMatches) {
      if (compatible.size === 0) {
        compatible.add(m.guid);
      } else {
        const common = commonPlayerGuids(compatible);
        if (
          (m.player1Guid && common.has(m.player1Guid)) ||
          (m.player2Guid && common.has(m.player2Guid))
        ) {
          compatible.add(m.guid);
        }
      }
    }
    setSelectedGuids(compatible);
  };

  const goToAnalysis = (page, rowGuid) => {
    const useSelection = selectedGuids.size > 1 && selectedGuids.has(rowGuid);
    const guids = useSelection ? [...selectedGuids] : [rowGuid];

    let playerGuid = null;
    if (useSelection) {
      const common = commonPlayerGuids(selectedGuids);
      playerGuid = common.size > 0 ? [...common][0] : null;
    } else {
      const m = matches.find((x) => x.guid === rowGuid);
      if (m?.isDoubles) {
        playerGuid = teamGuid(m.teamA) ?? null;
      } else {
        playerGuid = m?.player1Guid ?? null;
      }
    }

    navigate(page, { state: { matchGuids: guids, playerGuid } });
  };

  const filteredMatches = matches.filter((m) => {
    const playerStr =
      `${m.player1FirstName ?? ""} ${m.player1LastName ?? ""} ${m.player2FirstName ?? ""} ${m.player2LastName ?? ""}`.toLowerCase();
    const tournamentStr = (m.tournament?.name ?? "").toLowerCase();
    const matchTs = m.matchDate ?? 0;

    // Shared-with-me matches only appear when the "Shared with me" toggle is on.
    if (sharedOnly ? !m.shared : m.shared) return false;
    if (filters.player && !playerStr.includes(filters.player.toLowerCase()))
      return false;
    if (
      filters.tournament &&
      !tournamentStr.includes(filters.tournament.toLowerCase())
    )
      return false;
    if (filters.dateFrom && matchTs < new Date(filters.dateFrom).getTime())
      return false;
    if (
      filters.dateTo &&
      matchTs > new Date(filters.dateTo).getTime() + 86399999
    )
      return false;
    return true;
  });

  const matchLabel = (m) => {
    const p1 = `${m.player1FirstName ?? ""} ${m.player1LastName ?? ""}`.trim();
    const p2 = `${m.player2FirstName ?? ""} ${m.player2LastName ?? ""}`.trim();
    const date = m.matchDate ? new Date(m.matchDate).toLocaleDateString() : "";
    return `${p1 || "?"} vs ${p2 || "?"}${date ? " — " + date : ""}`;
  };

  // Navigate to the coding screen for a match (used by table + mobile cards).
  // The list rows don't carry the match's sets/events, so hydrate the full
  // match first — otherwise the coding screen treats it as empty and would
  // overwrite the stored events on exit.
  const openCoding = async (m) => {
    const full = (await fetchMatchWithData(uid, m.guid)) ?? m;
    if (!full.sets) full.sets = [];
    if (full.isDoubles) {
      // Doubles matches carry player info under teamA/teamB, not the
      // player1FirstName/player1LastName/player1Guid singles-only fields —
      // using those (all undefined here) left player1/player2 with no
      // firstName/lastName, crashing Scoreboard's `.substring()` call.
      const teamLabel = (team) =>
        [team?.player1?.lastName, team?.player2?.lastName].filter(Boolean).join("/") ||
        team?.name ||
        "Team";
      // Many ad-hoc doubles pairings never get a persisted team guid, so
      // team.guid itself is frequently undefined. Every toggle/rotation
      // comparison in CodingPage.jsx (toggleServer, togglePlayerSide, the
      // doubles hitter-team resolution) relies on player1.guid/player2.guid
      // — and match.teamA.guid/match.teamB.guid — actually differing; two
      // `undefined`s compare equal, which made those comparisons always
      // resolve the same way regardless of the real team. Fall back to a
      // stable synthetic guid (matches Matches.jsx/Teams.jsx/pstsFile.js's
      // own teamGuid() convention) and persist it onto the team itself so
      // every reference (not just player1/player2) sees the same value.
      const teamGuid = (team) =>
        team?.guid ||
        (team?.player1?.guid && team?.player2?.guid
          ? `${team.player1.guid}_${team.player2.guid}`
          : null);
      const teamA = full.teamA ?? m.teamA;
      const teamB = full.teamB ?? m.teamB;
      if (teamA && !teamA.guid) teamA.guid = teamGuid(teamA);
      if (teamB && !teamB.guid) teamB.guid = teamGuid(teamB);
      full.teamA = teamA;
      full.teamB = teamB;
      full.player1 = {
        firstName: "",
        lastName: teamLabel(teamA),
        guid: teamA?.guid,
      };
      full.player2 = {
        firstName: "",
        lastName: teamLabel(teamB),
        guid: teamB?.guid,
      };
    } else {
      full.player1 = {
        firstName: full.player1FirstName ?? m.player1FirstName,
        lastName: full.player1LastName ?? m.player1LastName,
        guid: full.player1Guid ?? m.player1Guid,
        country: {
          name: "",
          code: full.player1CountryCode ?? m.player1CountryCode,
        },
      };
      full.player2 = {
        firstName: full.player2FirstName ?? m.player2FirstName,
        lastName: full.player2LastName ?? m.player2LastName,
        guid: full.player2Guid ?? m.player2Guid,
        country: {
          name: "",
          code: full.player2CountryCode ?? m.player2CountryCode,
        },
      };
    }
    navigate("/match-coding", {
      state: { player1: full.player1, player2: full.player2, match: full },
    });
  };

  // Shared action buttons for a match row/card.
  const renderRowActions = (m) => {
    const multi = selectedGuids.size > 1 && selectedGuids.has(m.guid);
    return (
      <div className="flex gap-1 justify-center">
        {!m.shared && (
          <button className="btn btn-ghost btn-xs" title="Code match" onClick={() => openCoding(m)}>
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
        {/* <button
          className="btn btn-ghost btn-xs"
          title={multi ? `Match stats (${selectedGuids.size} matches)` : "Match stats"}
          onClick={() => goToAnalysis("/stats", m.guid)}
        >
          <ChartBarIcon className="w-4 h-4" />
        </button> */}
        <button
          className="btn btn-ghost btn-xs"
          title={multi ? `General report (${selectedGuids.size} matches)` : "General report"}
          onClick={() => goToAnalysis("/report", m.guid)}
        >
          <DocumentChartBarIcon className="w-4 h-4" />
        </button>
        <button
          className="btn btn-ghost btn-xs"
          title={multi ? `Stats analysis (${selectedGuids.size} matches)` : "Stats analysis"}
          onClick={() => goToAnalysis("/filters-analysis", m.guid)}
        >
          <FunnelIcon className="w-4 h-4" />
        </button>
        <button
          className="btn btn-ghost btn-xs"
          title={multi ? `Video analysis (${selectedGuids.size} matches)` : "Video analysis"}
          onClick={() => goToAnalysis("/video-analysis", m.guid)}
        >
          <VideoCameraIcon
            className={`w-4 h-4 ${m.videoFile ? "text-success" : "text-base-content"}`}
          />
        </button>
        <button
          className="btn btn-ghost btn-xs"
          title={
            multi
              ? `Share (${selectedGuids.size} matches)`
              : m.shared
                ? "Re-share this match"
                : m.shareStatus
                  ? "Shared — edit sharing"
                  : "Share match"
          }
          onClick={() => openShare(multi ? [...selectedGuids] : [m.guid])}
        >
          <ShareIcon
            className={`w-4 h-4 ${!m.shared && m.shareStatus ? "text-success" : "text-base-content"}`}
          />
        </button>
        {!m.shared && (
          <button
            className="btn btn-ghost btn-xs text-error"
            title="Delete match"
            onClick={() => setConfirmDeleteGuid(m.guid)}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Mobile card for a match.
  const renderMatchCard = (m) => (
    <div
      key={m.guid}
      className={`rounded-box border p-3 ${
        selectedGuids.has(m.guid)
          ? "border-primary bg-base-200"
          : "border-base-300 bg-base-100"
      }`}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          className="checkbox checkbox-sm mt-0.5 shrink-0"
          checked={selectedGuids.has(m.guid)}
          disabled={!isSelectable(m)}
          onChange={() => toggleSelect(m)}
        />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            {m.isDoubles ? m.teamA?.name || "—" : `${m.player1FirstName} ${m.player1LastName}`}
            <span className="text-base-content/40"> vs </span>
            {m.isDoubles ? m.teamB?.name || "—" : `${m.player2FirstName} ${m.player2LastName}`}
          </div>
          <div className="text-xs text-base-content/60 mt-0.5">
            {m.matchDate ? new Date(m.matchDate).toLocaleDateString() : "—"}
            {m.tournament?.name ? ` · ${m.tournament.name}` : ""}
          </div>
          {m.shared && (
            <div className="text-xs text-info flex items-center gap-1 mt-0.5">
              <ShareIcon className="w-3 h-3" />
              Shared{m.sharedBy ? ` by ${m.sharedBy}` : ""}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 border-t border-base-300 pt-2">{renderRowActions(m)}</div>
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] overflow-hidden container mx-auto px-4 pt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 shrink-0">
        <h1 className="text-2xl font-bold">Matches</h1>
        <div className="flex flex-wrap gap-2">
          {/* Single file or multi-file import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".psts"
            multiple
            className="hidden"
            onClick={(e) => { e.target.value = null; }}
            onChange={(e) => processFiles(e.target.files)}
          />
          {/* Folder import */}
          <input
            ref={folderInputRef}
            type="file"
            className="hidden"
            // eslint-disable-next-line react/no-unknown-property
            webkitdirectory=""
            onClick={(e) => { e.target.value = null; }}
            onChange={(e) => processFiles(e.target.files)}
          />
          {/* <button
            className="btn btn-outline btn-sm"
            onClick={() => openShare([...selectedGuids])}
            disabled={loading || selectedGuids.size === 0}
            title="Share selected matches"
          >
            <ShareIcon className="w-4 h-4" />
            Share{selectedGuids.size > 0 ? ` (${selectedGuids.size})` : ""}
          </button> */}
          <button
            className={`btn btn-sm ${sharedOnly ? "btn-success" : "btn-outline"}`}
            onClick={() => setSharedOnly((v) => !v)}
            disabled={loading}
            title={
              sharedOnly
                ? "Show all matches"
                : "Show only matches shared with me"
            }
          >
            <ShareIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Shared with me&nbsp;</span>
            <span>({matches.filter((m) => m.shared).length})</span>
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => fileInputRef.current.click()}
            disabled={loading || progress.total > 0}
            title="Import File(s)"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Import File(s)</span>
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => folderInputRef.current.click()}
            disabled={loading || progress.total > 0}
            title="Import Folder"
          >
            <FolderPlusIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Import Folder</span>
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setShowNewMatchModal(true)}
            disabled={loading}
            title="New Match"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:inline">New Match</span>
          </button>
        </div>
      </div>

      {/* Import log panel */}
      {showImportLog && importLog.length > 0 && (
        <div className="rounded-box border border-base-300 bg-base-100 p-3 mb-4 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">Import Log</span>
              {progress.total > 0 && (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  <span className="text-xs text-base-content/60">
                    {progress.current} / {progress.total}
                  </span>
                </>
              )}
            </div>
            {progress.total === 0 && (
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => setShowImportLog(false)}
              >
                ✕
              </button>
            )}
          </div>
          {progress.total > 0 && (
            <progress
              className="progress progress-primary w-full mb-2"
              value={progress.current}
              max={progress.total}
            />
          )}
          <ul className="space-y-1 max-h-52 overflow-y-auto">
            {importLog.map((entry, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                {entry.status === "parsing" || entry.status === "checking" ? (
                  <span className="loading loading-spinner loading-xs text-primary shrink-0" />
                ) : (
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      entry.status === "saved"
                        ? "bg-success"
                        : entry.status === "duplicate"
                          ? "bg-warning"
                          : entry.status === "error"
                            ? "bg-error"
                            : "bg-base-300"
                    }`}
                  />
                )}
                <span
                  className={`flex-1 truncate ${
                    entry.status === "error"
                      ? "text-error"
                      : "text-base-content/80"
                  }`}
                >
                  {entry.fileName}
                </span>
                <span className="text-xs shrink-0">
                  {entry.status === "parsing" && (
                    <span className="text-primary">Parsing…</span>
                  )}
                  {entry.status === "checking" && (
                    <span className="text-primary">Checking…</span>
                  )}
                  {entry.status === "pending" && (
                    <span className="text-base-content/40">Pending</span>
                  )}
                  {entry.status === "saved" && (
                    <span className="text-success">Saved</span>
                  )}
                  {entry.status === "duplicate" && (
                    <span className="text-warning">Duplicate</span>
                  )}
                  {entry.status === "skipped" && (
                    <span className="text-base-content/50">Skipped</span>
                  )}
                  {entry.status === "error" && (
                    <span className="text-error">{entry.error || "Error"}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters — collapsible: player search always shown, rest on expand */}
      <div className="bg-base-200 rounded-box p-3 mb-4 shrink-0">
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Search player…"
            className="input input-bordered input-sm flex-1"
            value={filters.player}
            onChange={(e) => setFilter("player", e.target.value)}
          />
          <button
            className="btn btn-ghost btn-sm gap-1"
            onClick={() => setFiltersExpanded((v) => !v)}
            title={filtersExpanded ? "Hide filters" : "More filters"}
          >
            <FunnelIcon className="w-4 h-4" />
            {(filters.tournament || filters.dateFrom || filters.dateTo) && (
              <span className="badge badge-primary badge-xs" />
            )}
            {filtersExpanded ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
        </div>

        {filtersExpanded && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            <input
              type="text"
              placeholder="Tournament…"
              className="input input-bordered input-sm w-full"
              value={filters.tournament}
              onChange={(e) => setFilter("tournament", e.target.value)}
            />
            <input
              type="date"
              className="input input-bordered input-sm w-full"
              title="Date from"
              value={filters.dateFrom}
              onChange={(e) => setFilter("dateFrom", e.target.value)}
            />
            <div className="flex gap-1">
              <input
                type="date"
                className="input input-bordered input-sm flex-1"
                title="Date to"
                value={filters.dateTo}
                onChange={(e) => setFilter("dateTo", e.target.value)}
              />
              <button
                className="btn btn-ghost btn-sm"
                title="Clear filters"
                onClick={clearFilters}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Matches table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : filteredMatches.length === 0 ? (
        <p className="text-center text-base-content/50 py-8">
          {matches.length === 0
            ? "No matches yet. Import a .psts file to get started."
            : "No matches match the current filters."}
        </p>
      ) : (
        <div className="flex flex-col flex-1 overflow-hidden">
          <p className="text-sm text-base-content/50 mb-1 shrink-0">
            {filteredMatches.length} of {matches.length} match(es)
            {selectedGuids.size > 0 && (
              <span className="ml-2 badge badge-primary badge-sm">
                {selectedGuids.size} selected
              </span>
            )}
          </p>

          {/* Mobile: card list */}
          <div className="md:hidden flex-1 overflow-y-auto space-y-2 pb-4">
            {filteredMatches.map((m) => renderMatchCard(m))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto overflow-y-auto flex-1">
          <table className="table table-zebra w-full">
            <thead className="sticky top-0 z-10 bg-base-200">
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={
                      filteredMatches.length > 0 &&
                      selectedGuids.size === filteredMatches.length
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Date</th>
                <th>Player 1 / Team A</th>
                <th>Player 2 / Team B</th>
                <th>Tournament</th>
                <th className="w-20 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map((m) => (
                <tr
                  key={m.guid}
                  className={`hover${selectedGuids.has(m.guid) ? " bg-base-200" : ""}`}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedGuids.has(m.guid)}
                      disabled={!isSelectable(m)}
                      onChange={() => toggleSelect(m)}
                    />
                  </td>
                  <td>
                    {m.matchDate
                      ? new Date(m.matchDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    {m.isDoubles
                      ? (m.teamA?.name || "—")
                      : `${m.player1FirstName} ${m.player1LastName}`}
                    {m.shared && (
                      <div className="text-xs text-info flex items-center gap-1">
                        <ShareIcon className="w-3 h-3" />
                        Shared{m.sharedBy ? ` by ${m.sharedBy}` : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    {m.isDoubles
                      ? (m.teamB?.name || "—")
                      : `${m.player2FirstName} ${m.player2LastName}`}
                  </td>
                  <td>
                    <div>{m.tournament?.name || "—"}</div>
                    {(m.round?.notes || m.roundName) && (
                      <div className="text-xs text-base-content/50">
                        {m.round?.notes ||
                          (typeof m.roundName === "string"
                            ? m.roundName
                            : (m.roundName?.notes ?? ""))}
                      </div>
                    )}
                  </td>
                  <td>{renderRowActions(m)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDeleteGuid && (() => {
        const m = matches.find((x) => x.guid === confirmDeleteGuid);
        return (
          <div className="modal modal-open z-50">
            <div className="modal-box max-w-sm">
              <h3 className="font-bold text-lg mb-2">Delete Match?</h3>
              <p className="text-sm mb-4">
                {m ? matchLabel(m) : confirmDeleteGuid}
              </p>
              <p className="text-xs text-base-content/60 mb-4">
                This will permanently delete the match record and its event data. This cannot be undone.
              </p>
              <div className="modal-action">
                <button className="btn btn-ghost btn-sm" disabled={deleting} onClick={() => setConfirmDeleteGuid(null)}>Cancel</button>
                <button className="btn btn-error btn-sm" disabled={deleting} onClick={handleDeleteMatch}>
                  {deleting ? <span className="loading loading-spinner loading-xs" /> : "Delete"}
                </button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={() => !deleting && setConfirmDeleteGuid(null)} />
          </div>
        );
      })()}

      {/* New match modal */}
      {showNewMatchModal && (
        <NewMatchModal
          uid={uid}
          players={players}
          teams={teams}
          existingTournaments={existingTournaments}
          onSave={handleCreateMatch}
          onClose={() => setShowNewMatchModal(false)}
        />
      )}

      {/* Share matches modal */}
      {shareTargets && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg mb-3">
              {shareTargets.length > 1
                ? `Share ${shareTargets.length} Matches`
                : "Share Match"}
            </h3>
            <Share
              shareStatus={shareInitial().shareStatus}
              shareUsers={shareInitial().shareUsers}
              targetCount={shareTargets.length}
              sharedBy={shareInitial().sharedBy}
              onShare={handleShare}
            />
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !savingShare && setShareTargets(null)}
          />
        </div>
      )}

      {/* Duplicate matches modal */}
      {showDuplicateModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Duplicate Matches Found</h3>
            <p className="py-2">
              {duplicates.length} match(es) already exist in the database:
            </p>
            <ul className="list-disc list-inside max-h-48 overflow-y-auto text-sm mb-2">
              {duplicates.map((item, i) => (
                <li key={i}>{matchLabel(item.match)}</li>
              ))}
            </ul>
            <p className="text-sm text-base-content/70 mb-4">
              Do you want to overwrite the existing records or skip them?
            </p>
            <div className="modal-action">
              <button className="btn btn-error" onClick={handleOverwriteAll}>
                Overwrite All
              </button>
              <button className="btn" onClick={handleSkipAll}>
                Skip All
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={handleSkipAll} />
        </div>
      )}

      {/* Player conflict modal */}
      {showPlayerConflictModal && (
        <PlayerConflictModal
          conflicts={playerConflicts}
          resolving={resolvingConflicts}
          onConfirm={handleResolvePlayerConflicts}
          onCancel={() => {
            setShowPlayerConflictModal(false);
            setPlayerConflicts([]);
            loadMatches();
          }}
        />
      )}
    </div>
  );
}

// ── PlayerConflictModal ──────────────────────────────────────────────────────

function PlayerConflictModal({ conflicts, resolving, onConfirm, onCancel }) {
  const [choices, setChoices] = useState(() =>
    Object.fromEntries(conflicts.map((c) => [c.pstsPlayer.guid, "existing"]))
  );

  const setAll = (val) =>
    setChoices(Object.fromEntries(conflicts.map((c) => [c.pstsPlayer.guid, val])));

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-1">Duplicate Player Names</h3>
        <p className="text-sm text-base-content/70 mb-3">
          The imported file(s) contain player(s) already saved under a different ID.
          The new player records have been created. Choose whether to merge each one into
          the existing record (recommended) or keep both as separate players.
        </p>
        <div className="flex gap-2 mb-3">
          <button className="btn btn-xs btn-outline" onClick={() => setAll("existing")}>
            Merge All into Existing
          </button>
          <button className="btn btn-xs btn-outline" onClick={() => setAll("new")}>
            Keep All Separate
          </button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {conflicts.map((c) => {
            const name = `${c.pstsPlayer.firstName ?? ""} ${c.pstsPlayer.lastName ?? ""}`.trim();
            return (
              <div key={c.pstsPlayer.guid} className="border border-base-300 rounded-box p-3">
                <p className="font-semibold text-sm mb-1">{name}</p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name={`pc-${c.pstsPlayer.guid}`}
                      className="radio radio-sm radio-primary"
                      checked={choices[c.pstsPlayer.guid] === "existing"}
                      onChange={() =>
                        setChoices((prev) => ({ ...prev, [c.pstsPlayer.guid]: "existing" }))
                      }
                    />
                    Merge into existing
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name={`pc-${c.pstsPlayer.guid}`}
                      className="radio radio-sm"
                      checked={choices[c.pstsPlayer.guid] === "new"}
                      onChange={() =>
                        setChoices((prev) => ({ ...prev, [c.pstsPlayer.guid]: "new" }))
                      }
                    />
                    Keep separate
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <div className="modal-action">
          <button className="btn btn-ghost btn-sm" disabled={resolving} onClick={onCancel}>
            Skip
          </button>
          <button
            className="btn btn-primary btn-sm"
            disabled={resolving}
            onClick={() => onConfirm(choices)}
          >
            {resolving ? <span className="loading loading-spinner loading-xs" /> : "Confirm"}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" />
    </div>
  );
}

// ── PlayerSearch ─────────────────────────────────────────────────────────────

function PlayerSearch({ label, players, selected, onSelect, excludeGuid, onNewPlayer }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = players
    .filter((p) => p.guid !== excludeGuid)
    .filter((p) =>
      `${p.firstName ?? ""} ${p.lastName ?? ""}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    )
    .slice(0, 20);

  const displayName = selected
    ? `${selected.firstName ?? ""} ${selected.lastName ?? ""}`.trim()
    : "";

  return (
    <div>
      <div className="label py-0.5 flex items-center justify-between">
        <span className="label-text text-xs font-semibold">{label}</span>
        {onNewPlayer && (
          <button type="button" className="btn btn-ghost btn-xs p-0.5" title="Create new player" onClick={onNewPlayer}>
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Search player…"
          value={open ? search : displayName}
          onFocus={() => {
            setSearch("");
            setOpen(true);
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (
          <ul className="absolute z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-full max-h-40 overflow-y-auto mt-0.5">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-base-content/50">
                No players found
              </li>
            ) : (
              filtered.map((p) => (
                <li
                  key={p.guid}
                  className="px-3 py-1.5 text-sm hover:bg-base-200 cursor-pointer"
                  onMouseDown={() => {
                    onSelect(p);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {p.firstName} {p.lastName}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── QuickNewPlayerPanel ───────────────────────────────────────────────────────

function QuickNewPlayerPanel({ uid, onCreated, onCancel }) {
  const [fields, setFields] = useState({ firstName: "", lastName: "", hand: "0", gender: "Male" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));
  const canCreate = fields.firstName.trim() || fields.lastName.trim();

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    const player = {
      guid: uuid(),
      uid,
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      hand: parseInt(fields.hand, 10),
      gender: fields.gender,
      dob: 0, height: 0, weight: 0, email: "", country: null, photoUrl: "",
    };
    await savePlayer(player);
    setSaving(false);
    onCreated(player);
  };

  return (
    <div className="bg-base-200 rounded-box p-3 flex flex-col gap-2 border border-primary/30">
      <div className="text-xs font-semibold text-primary uppercase tracking-wide">New Player</div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input input-bordered input-xs" placeholder="First name" value={fields.firstName}
          onChange={(e) => set("firstName", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()} autoFocus />
        <input className="input input-bordered input-xs" placeholder="Last name" value={fields.lastName}
          onChange={(e) => set("lastName", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select className="select select-bordered select-xs" value={fields.hand} onChange={(e) => set("hand", e.target.value)}>
          <option value="0">Right-handed</option>
          <option value="1">Left-handed</option>
          <option value="2">Unknown hand</option>
        </select>
        <select className="select select-bordered select-xs" value={fields.gender} onChange={(e) => set("gender", e.target.value)}>
          <option>Male</option><option>Female</option><option>Unknown</option>
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="btn btn-ghost btn-xs" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-xs" disabled={!canCreate || saving} onClick={handleCreate}>
          {saving && <span className="loading loading-spinner loading-xs" />}Create
        </button>
      </div>
    </div>
  );
}

// ── TeamSearch ────────────────────────────────────────────────────────────────

function teamDisplayLabel(team) {
  if (!team) return "";
  if (team.name) return team.name;
  return [team.player1?.lastName, team.player2?.lastName].filter(Boolean).join(" / ");
}

function TeamSearch({ label, teams, selected, onSelect, excludeGuid, onNewTeam }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = teams
    .filter((t) => t.guid !== excludeGuid)
    .filter((t) => teamDisplayLabel(t).toLowerCase().includes(search.toLowerCase()))
    .slice(0, 20);

  const displayName = selected ? teamDisplayLabel(selected) : "";

  return (
    <div>
      <div className="label py-0.5 flex items-center justify-between">
        <span className="label-text text-xs font-semibold">{label}</span>
        {onNewTeam && (
          <button type="button" className="btn btn-ghost btn-xs p-0.5" title="Create new team" onClick={onNewTeam}>
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Search team…"
          value={open ? search : displayName}
          onFocus={() => {
            setSearch("");
            setOpen(true);
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (
          <ul className="absolute z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-full max-h-40 overflow-y-auto mt-0.5">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-base-content/50">
                No teams found
              </li>
            ) : (
              filtered.map((t) => (
                <li
                  key={t.guid}
                  className="px-3 py-1.5 text-sm hover:bg-base-200 cursor-pointer"
                  onMouseDown={() => {
                    onSelect(t);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {teamDisplayLabel(t)}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── QuickNewTeamPanel ─────────────────────────────────────────────────────────

function QuickNewTeamPanel({ uid, players, onCreated, onCancel }) {
  const [localPlayers, setLocalPlayers] = useState(players);
  const [name, setName] = useState("");
  const [teamPlayer1, setTeamPlayer1] = useState(null);
  const [teamPlayer2, setTeamPlayer2] = useState(null);
  const [quickNewPlayer, setQuickNewPlayer] = useState(null); // 't1' | 't2' | null
  const [saving, setSaving] = useState(false);

  const handlePlayerCreated = (slot, newPlayer) => {
    setLocalPlayers((prev) => (prev.find((p) => p.guid === newPlayer.guid) ? prev : [...prev, newPlayer]));
    if (slot === "t1") setTeamPlayer1(newPlayer);
    else setTeamPlayer2(newPlayer);
    setQuickNewPlayer(null);
  };

  const canCreate = teamPlayer1 && teamPlayer2 && teamPlayer1.guid !== teamPlayer2.guid;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    const team = {
      uid,
      guid: `${teamPlayer1.guid}_${teamPlayer2.guid}`,
      name: name.trim() || [teamPlayer1.lastName, teamPlayer2.lastName].filter(Boolean).join(" / "),
      player1: teamPlayer1,
      player2: teamPlayer2,
      notes: {},
    };
    await saveTeam(team);
    setSaving(false);
    onCreated(team);
  };

  return (
    <div className="bg-base-200 rounded-box p-3 flex flex-col gap-2 border border-primary/30">
      <div className="text-xs font-semibold text-primary uppercase tracking-wide">New Team</div>
      <input
        type="text"
        className="input input-bordered input-xs"
        placeholder="Team name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <PlayerSearch
          label="Player A"
          players={localPlayers}
          selected={teamPlayer1}
          onSelect={setTeamPlayer1}
          excludeGuid={teamPlayer2?.guid}
          onNewPlayer={() => setQuickNewPlayer("t1")}
        />
        <PlayerSearch
          label="Player B"
          players={localPlayers}
          selected={teamPlayer2}
          onSelect={setTeamPlayer2}
          excludeGuid={teamPlayer1?.guid}
          onNewPlayer={() => setQuickNewPlayer("t2")}
        />
      </div>
      {quickNewPlayer && (
        <QuickNewPlayerPanel
          uid={uid}
          onCreated={(p) => handlePlayerCreated(quickNewPlayer, p)}
          onCancel={() => setQuickNewPlayer(null)}
        />
      )}
      <div className="flex gap-2 justify-end">
        <button className="btn btn-ghost btn-xs" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-xs" disabled={!canCreate || saving} onClick={handleCreate}>
          {saving && <span className="loading loading-spinner loading-xs" />}Create
        </button>
      </div>
    </div>
  );
}

// ── NewMatchModal ─────────────────────────────────────────────────────────────

function NewMatchModal({ players, teams, existingTournaments, onSave, onClose, uid = null, defaultPlayer1 = null }) {
  const today = new Date().toISOString().slice(0, 10);
  const [localPlayers, setLocalPlayers] = useState(players);
  const [localTeams, setLocalTeams] = useState(teams ?? []);
  const [isDoubles, setIsDoubles] = useState(false);
  const [player1, setPlayer1] = useState(defaultPlayer1);
  const [player2, setPlayer2] = useState(null);
  const [team1, setTeam1] = useState(null);
  const [team2, setTeam2] = useState(null);
  const [matchDate, setMatchDate] = useState(today);
  const [tournamentName, setTournamentName] = useState("");
  const [tournamentOpen, setTournamentOpen] = useState(false);
  const [round, setRound] = useState("R1");
  const [numSets, setNumSets] = useState(3);
  const [setConfigs, setSetConfigs] = useState(() =>
    Array.from({ length: 3 }, () => ({ ...DEFAULT_SET_CONFIG })),
  );
  // No-Ad scoring: at deuce (40-40) the next point wins the game outright
  // instead of requiring a two-point margin.
  const [noAd, setNoAd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickNewPlayer, setQuickNewPlayer] = useState(null); // 'p1' | 'p2' | null
  const [quickNewTeam, setQuickNewTeam] = useState(null); // 't1' | 't2' | null
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [newTournamentName, setNewTournamentName] = useState("");

  const handlePlayerCreated = (slot, newPlayer) => {
    setLocalPlayers((prev) => prev.find((p) => p.guid === newPlayer.guid) ? prev : [...prev, newPlayer]);
    if (slot === "p1") setPlayer1(newPlayer);
    else setPlayer2(newPlayer);
    setQuickNewPlayer(null);
  };

  const handleTeamCreated = (slot, newTeam) => {
    setLocalTeams((prev) => prev.find((t) => t.guid === newTeam.guid) ? prev : [...prev, newTeam]);
    if (slot === "t1") setTeam1(newTeam);
    else setTeam2(newTeam);
    setQuickNewTeam(null);
  };

  const updateNumSets = (n) => {
    setNumSets(n);
    setSetConfigs((prev) =>
      Array.from({ length: n }, (_, i) => prev[i] ?? { ...DEFAULT_SET_CONFIG }),
    );
  };

  const updateSetConfig = (idx, field, rawValue) => {
    const value = parseInt(rawValue, 10);
    if (isNaN(value)) return;
    setSetConfigs((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const tournamentSuggestions = tournamentName.trim()
    ? existingTournaments.filter((t) =>
        t.toLowerCase().includes(tournamentName.toLowerCase()),
      )
    : existingTournaments;

  const canSave = isDoubles
    ? team1 && team2 && team1.guid !== team2.guid && matchDate
    : player1 && player2 && player1.guid !== player2.guid && matchDate;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const dateTs = new Date(matchDate).getTime();
    const roundInfo = ROUNDS.find((r) => r.label === round) ?? {
      label: round,
      number: 0,
    };
    const match = {
      guid: isDoubles
        ? `${team1.guid}_${team2.guid}_${dateTs}`
        : `${player1.guid}_${player2.guid}_${dateTs}`,
      isDoubles,
      isPractice: false,
      matchDate: dateTs,
      videoStartTime: 0,
      scoreModeAdvantage: noAd ? 1 : 0,
      scoreModeTiebreak: 0,
      roundName: round,
      temperature: 0,
      videoFile: "",
      importedFrom: "manual",
      coder: "",
      tournament: tournamentName.trim()
        ? {
            name: tournamentName.trim(),
            guid: `tournament_${tournamentName.trim().toLowerCase().replace(/\s+/g, "_")}`,
            startDate: 0,
            enddate: 0,
            gender: "",
            prizeMoney: 0,
            code: "",
            year: new Date(matchDate).getFullYear(),
            country: null,
          }
        : null,
      court: null,
      round: { notes: round, number: roundInfo.number },
      weather: null,
      ...(isDoubles
        ? {
            teamA: { name: team1.name ?? "", guid: team1.guid, player1: team1.player1, player2: team1.player2 },
            teamB: { name: team2.name ?? "", guid: team2.guid, player1: team2.player1, player2: team2.player2 },
          }
        : {
            player1: { ...player1 },
            player1Guid: player1.guid,
            player1FirstName: player1.firstName ?? "",
            player1LastName: player1.lastName ?? "",
            player1CountryCode: player1.country?.code ?? "UNK",
            player2: { ...player2 },
            player2Guid: player2.guid,
            player2FirstName: player2.firstName ?? "",
            player2LastName: player2.lastName ?? "",
            player2CountryCode: player2.country?.code ?? "UNK",
          }),
      sets: setConfigs.map((cfg, i) => ({
        number: i + 1,
        currentSkill: 0,
        isTiebreak: false,
        oppositionGameScore: 0,
        oppositionScore: 0,
        playerGameScore: 0,
        playerScore: 0,
        serves: 0,
        servingZone: 0,
        onTopCourt: false,
        user1: "",
        user2: "",
        tiebreakAfterGame: cfg.tiebreakAfterGame,
        tiebreakTo: cfg.tiebreakTo,
        tiebreakMargin: cfg.tiebreakMargin,
        events: [],
      })),
      videos: [],
      uid,
    };
    await onSave(match);
    setSaving(false);
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <h3 className="font-bold text-lg">New Match</h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 flex flex-col gap-4 pr-1">
          {/* Doubles toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={isDoubles}
              onChange={(e) => setIsDoubles(e.target.checked)}
            />
            <span className="text-sm font-semibold">Doubles match</span>
          </label>

          {/* Players / Teams */}
          {isDoubles ? (
            <div className="grid grid-cols-2 gap-3">
              <TeamSearch
                label="Team 1"
                teams={localTeams}
                selected={team1}
                onSelect={setTeam1}
                excludeGuid={team2?.guid}
                onNewTeam={() => setQuickNewTeam("t1")}
              />
              <TeamSearch
                label="Team 2"
                teams={localTeams}
                selected={team2}
                onSelect={setTeam2}
                excludeGuid={team1?.guid}
                onNewTeam={() => setQuickNewTeam("t2")}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {defaultPlayer1 ? (
                <div>
                  <div className="label py-0.5">
                    <span className="label-text text-xs font-semibold">Player 1</span>
                  </div>
                  <div className="input input-bordered input-sm w-full bg-base-200 text-base-content/60 cursor-not-allowed flex items-center">
                    {`${defaultPlayer1.firstName ?? ""} ${defaultPlayer1.lastName ?? ""}`.trim() || "—"}
                  </div>
                </div>
              ) : (
                <PlayerSearch
                  label="Player 1"
                  players={localPlayers}
                  selected={player1}
                  onSelect={setPlayer1}
                  excludeGuid={player2?.guid}
                  onNewPlayer={() => setQuickNewPlayer("p1")}
                />
              )}
              <PlayerSearch
                label="Player 2"
                players={localPlayers}
                selected={player2}
                onSelect={setPlayer2}
                excludeGuid={player1?.guid}
                onNewPlayer={() => setQuickNewPlayer("p2")}
              />
            </div>
          )}
          {!isDoubles && localPlayers.length === 0 && (
            <p className="text-xs text-warning">
              No players found. Import matches first to populate the player
              list.
            </p>
          )}

          {/* Quick new player panel */}
          {quickNewPlayer && (
            <QuickNewPlayerPanel
              uid={uid}
              onCreated={(p) => handlePlayerCreated(quickNewPlayer, p)}
              onCancel={() => setQuickNewPlayer(null)}
            />
          )}

          {/* Quick new team panel */}
          {quickNewTeam && (
            <QuickNewTeamPanel
              uid={uid}
              players={localPlayers}
              onCreated={(t) => handleTeamCreated(quickNewTeam, t)}
              onCancel={() => setQuickNewTeam(null)}
            />
          )}

          {/* Date + Round */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label py-0.5">
                <span className="label-text text-xs font-semibold">
                  Match Date
                </span>
              </div>
              <input
                type="date"
                className="input input-bordered input-sm w-full"
                value={matchDate}
                onChange={(e) => setMatchDate(e.target.value)}
              />
            </div>
            <div>
              <div className="label py-0.5">
                <span className="label-text text-xs font-semibold">Round</span>
              </div>
              <select
                className="select select-bordered select-sm w-full"
                value={round}
                onChange={(e) => setRound(e.target.value)}
              >
                {ROUNDS.map((r) => (
                  <option key={r.label} value={r.label}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tournament */}
          <div className="relative">
            <div className="label py-0.5 flex items-center justify-between">
              <span className="label-text text-xs font-semibold">
                Tournament (optional)
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs p-0.5"
                title="Add new tournament"
                onClick={() => { setShowTournamentForm((v) => !v); setNewTournamentName(tournamentName); }}
              >
                <PlusIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter or select tournament…"
              value={tournamentName}
              onChange={(e) => {
                setTournamentName(e.target.value);
                setTournamentOpen(true);
              }}
              onFocus={() => setTournamentOpen(true)}
              onBlur={() => setTimeout(() => setTournamentOpen(false), 150)}
            />
            {tournamentOpen && tournamentSuggestions.length > 0 && (
              <ul className="absolute z-50 bg-base-100 border border-base-300 rounded-box shadow-lg w-full max-h-36 overflow-y-auto mt-0.5">
                {tournamentSuggestions.map((t) => (
                  <li
                    key={t}
                    className="px-3 py-1.5 text-sm hover:bg-base-200 cursor-pointer"
                    onMouseDown={() => {
                      setTournamentName(t);
                      setTournamentOpen(false);
                    }}
                  >
                    {t}
                  </li>
                ))}
              </ul>
            )}
            {showTournamentForm && (
              <div className="mt-1 bg-base-200 rounded-box p-2 flex gap-2 items-center border border-primary/30">
                <input
                  type="text"
                  className="input input-bordered input-xs flex-1"
                  placeholder="New tournament name"
                  value={newTournamentName}
                  onChange={(e) => setNewTournamentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newTournamentName.trim()) {
                      setTournamentName(newTournamentName.trim());
                      setShowTournamentForm(false);
                    }
                  }}
                  autoFocus
                />
                <button
                  className="btn btn-primary btn-xs"
                  disabled={!newTournamentName.trim()}
                  onClick={() => { setTournamentName(newTournamentName.trim()); setShowTournamentForm(false); }}
                >
                  Add
                </button>
                <button className="btn btn-ghost btn-xs" onClick={() => setShowTournamentForm(false)}>
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Number of sets */}
          <div>
            <div className="label py-0.5">
              <span className="label-text text-xs font-semibold">
                Number of Sets
              </span>
            </div>
            <div className="flex gap-2">
              {[1, 3, 5].map((n) => (
                <button
                  key={n}
                  className={`btn btn-sm ${numSets === n ? "btn-primary" : "btn-outline"}`}
                  onClick={() => updateNumSets(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Scoring format */}
          <div>
            <div className="label py-0.5">
              <span className="label-text text-xs font-semibold">
                Match Style
              </span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={noAd}
                onChange={(e) => setNoAd(e.target.checked)}
              />
              <span className="text-sm">
                No-Ad (deciding point at deuce, no advantage)
              </span>
            </label>
          </div>

          {/* Per-set tiebreak config */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
              Tiebreak Rules per Set
            </div>
            <table className="table table-xs w-full">
              <thead>
                <tr>
                  <th>Set</th>
                  <th>TB after game</th>
                  <th>TB to</th>
                  <th>TB margin</th>
                </tr>
              </thead>
              <tbody>
                {setConfigs.map((cfg, i) => (
                  <tr key={i}>
                    <td className="font-medium">Set {i + 1}</td>
                    <td>
                      <input
                        type="number"
                        className="input input-bordered input-xs w-16"
                        min={1}
                        max={99}
                        value={cfg.tiebreakAfterGame}
                        onChange={(e) =>
                          updateSetConfig(
                            i,
                            "tiebreakAfterGame",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="input input-bordered input-xs w-16"
                        min={1}
                        max={999}
                        value={cfg.tiebreakTo}
                        onChange={(e) =>
                          updateSetConfig(i, "tiebreakTo", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="input input-bordered input-xs w-16"
                        min={1}
                        max={10}
                        value={cfg.tiebreakMargin}
                        onChange={(e) =>
                          updateSetConfig(i, "tiebreakMargin", e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t border-base-300 shrink-0 mt-4">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {saving && <span className="loading loading-spinner loading-xs" />}
            Create Match
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
}

export default Matches;
